import React from 'react';
import {
  AiOutlineFolder,
  AiOutlineFile,
  AiOutlineFilePdf,
  AiOutlineFileWord,
  AiOutlineFileExcel,
  AiOutlineFilePpt,
  AiOutlineFileImage,
  AiOutlineFileZip,
  AiOutlineFileText,
} from 'react-icons/ai';

/**
 * 파일 타입에 따른 아이콘 컴포넌트를 반환하는 함수
 * @param {string} type - 파일 타입 또는 확장자
 * @returns {React.ReactElement} 아이콘 컴포넌트
 */
export const getFileIcon = (type) => {
  switch (type?.toLowerCase()) {
    case 'folder':
    case 'dir':
      return <AiOutlineFolder className="anticon" />;
    case 'pdf':
      return <AiOutlineFilePdf className="anticon" />;
    case 'doc':
    case 'docx':
      return <AiOutlineFileWord className="anticon" />;
    case 'xls':
    case 'xlsx':
      return <AiOutlineFileExcel className="anticon" />;
    case 'ppt':
    case 'pptx':
      return <AiOutlineFilePpt className="anticon" />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
      return <AiOutlineFileImage className="anticon" />;
    case 'zip':
    case 'rar':
    case '7z':
      return <AiOutlineFileZip className="anticon" />;
    case 'txt':
    case 'md':
      return <AiOutlineFileText className="anticon" />;
    default:
      return <AiOutlineFile className="anticon" />;
  }
};

/**
 * 파일명에서 확장자를 추출하는 함수
 * @param {string} filename - 파일명
 * @returns {string} 파일 확장자
 */
export const getFileExtension = (filename) => {
  if (!filename) return '';
  const parts = filename.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

/**
 * 파일명에 기반하여 아이콘 컴포넌트를 반환하는 함수
 * @param {string} filename - 파일명
 * @param {string} type - 파일 타입 ('file' 또는 'dir')
 * @returns {React.ReactElement} 아이콘 컴포넌트
 */
export const getIconByFilename = (filename, type) => {
  if (type === 'dir') return getFileIcon('folder');
  return getFileIcon(getFileExtension(filename));
};

/**
 * 바이트 크기를 읽기 쉬운 형식으로 변환
 * @param {number} bytes 바이트 크기
 * @returns {string} 포맷팅된 크기
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  // eslint-disable-next-line no-restricted-properties
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * 파일명에서 확장자를 제외한 베이스명 추출
 * @param {string} fileName - 파일명
 * @returns {string} 확장자를 제외한 파일명
 */
export const getBaseName = (fileName) => {
  if (!fileName) return '';
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex === -1 ? fileName : fileName.substring(0, lastDotIndex);
};

/**
 * 파일 경로에서 디렉토리 경로 추출
 * @param {string} filePath - 파일 경로
 * @returns {string} 디렉토리 경로
 */
export const extractDirectoryPath = (filePath) => {
  if (!filePath || !filePath.includes('/')) return '';
  const lastSlashIndex = filePath.lastIndexOf('/');
  return filePath.substring(0, lastSlashIndex);
};

/**
 * 파일 경로에서 루트 폴더명 추출 (첫 번째 경로)
 * @param {string} filePath - 파일 경로 (예: "folder/subfolder/file.txt")
 * @returns {string} 루트 폴더명 (예: "folder")
 */
export const extractRootFolderName = (filePath) => {
  if (!filePath || !filePath.includes('/')) return filePath;
  return filePath.split('/')[0];
};

/**
 * 파일 청크를 Base64로 변환
 * @param {File} file - 파일 객체
 * @param {number} start - 시작 바이트 위치
 * @param {number} end - 종료 바이트 위치
 * @returns {} Base64 인코딩된 청크
 */
export const readFileChunkAsBase64 = (file, start, end) => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      resolve(base64);
    };

    reader.onerror = () => {
      console.error('파일 청크 읽기 오류');
      resolve('');
    };

    try {
      const slice = file.slice(start, end);
      reader.readAsDataURL(slice);
    } catch (error) {
      console.error('파일 슬라이스 오류:', error);
      resolve('');
    }
  });
};
